import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Box, Typography, Button, CircularProgress, Paper, Fade } from '@mui/material';
import swal from 'sweetalert';
import { synthesisApi } from '../../services/synthesisApi';
import { useEntity } from '../../context/EntityContext';
import AnimatedBackdrop from '../../components/AnimatedBackdrop';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const AudioUpload: React.FC = () => {
  const { selectedEntity } = useEntity();
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<any>(null);

  // Callback para tratar o drop dos arquivos
  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles && acceptedFiles.length > 0) {
      // Consideramos apenas o primeiro arquivo, visto que o upload é único
      setFile(acceptedFiles[0]);
    }
  }, []);

  // Configuração do hook useDropzone
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'audio/*': [], 'video/mp4': ['.mp4'] },
    multiple: false,
  });

  // Função para enviar o áudio à API
  const handleUpload = async () => {
    if (!file) {
      swal("Erro", "Nenhum arquivo selecionado.", "error");
      return;
    }
    if (!selectedEntity) {
      swal("Erro", "Entidade não definida.", "error");
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      // Chama o endpoint de processar áudio: /entities/{selectedEntity}/process-audio
      const response = await synthesisApi.post(`/entities/${selectedEntity}/process-audio`, formData, {
        headers: {
          "Content-Type": "multipart/form-data"
        }
      });
      setResult(response.data);
    } catch (error: any) {
      console.error("Erro ao processar áudio:", error);
      swal("Erro", "Falha ao processar o áudio. Tente novamente.", "error");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {result ? (
        <Fade in={Boolean(result)} timeout={600} mountOnEnter unmountOnExit>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom sx={{ textAlign: "center" }}>
              {result.title}
            </Typography>
            <Box
              sx={{
                border: '1px solid #ccc',
                borderRadius: 1,
                p: 2,
                mt: 2,
                position: 'relative',
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  position: 'absolute',
                  top: '-10px',
                  left: '10px',
                  backgroundColor: 'white',
                  px: 1,
                }}
              >
                Resumo
              </Typography>
              <Typography variant="body1">
                {result.summary}
              </Typography>
            </Box>
            <Box sx={{ mt: 2 }}>
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {result.text}
              </ReactMarkdown>
            </Box>
          </Paper>
        </Fade>
      ) : (
        <Fade in={!result} timeout={600} mountOnEnter unmountOnExit>
          <div>
            <Typography variant="h4" gutterBottom>
              Upload de Áudio
            </Typography>
            <Paper sx={{ p: 3, mb: 3 }}>
              <Box
                {...getRootProps({
                  // Removido onClick para não interferir no comportamento padrão
                  role: "button",
                  "aria-label": "Área de upload de áudio",
                })}
                sx={{
                  border: "2px dashed #6A1B9A",
                  borderRadius: 2,
                  p: 4,
                  textAlign: "center",
                  cursor: "pointer",
                }}
              >
                <input {...getInputProps()} />
                {isDragActive ? (
                  <Typography variant="body1">
                    Solte o arquivo de áudio aqui...
                  </Typography>
                ) : (
                  <Typography variant="body1">
                    Arraste e solte um arquivo de áudio aqui, ou clique para selecionar.
                  </Typography>
                )}
                {file && (
                  <Typography variant="subtitle1" sx={{ mt: 2 }}>
                    Arquivo selecionado: {file.name}
                  </Typography>
                )}
              </Box>
              {uploading && (
                <AnimatedBackdrop
                  title="Entendendo a Sessão"
                  subtitle="Aguarde enquanto entendo o áudio da sessão e preparo o texto para você..."
                />
              )}
              <Box sx={{ textAlign: "center", mt: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleUpload}
                  disabled={uploading || !file}
                >
                  {uploading ? <CircularProgress size={24} /> : "Processar Áudio"}
                </Button>
              </Box>
            </Paper>
          </div>
        </Fade>
      )}
    </Box>
  );
};

export default AudioUpload;